import type { Currency } from './types';

export type AmountParseResult =
  | { ok: true; amount: number; currency: Currency }
  | { ok: false; code: 'empty' | 'no_amount' | 'unknown_currency' | 'non_positive' };

const SUPPORTED: ReadonlyArray<Currency> = ['AED', 'USD', 'GBP', 'EUR', 'CHF'];

const SYMBOL_TO_CURRENCY: Record<string, Currency> = {
  '£': 'GBP',
  '$': 'USD',
  '€': 'EUR',
};

/**
 * Parse a user-typed amount input. Accepts:
 *   - "400"           → AED 400
 *   - "gbp 400"       → GBP 400 (case-insensitive, space optional)
 *   - "£75" / "£ 75"  → GBP 75
 *   - "usd 50"        → USD 50
 * Returns a discriminated result. No throws.
 */
export function parseAmountInput(raw: string): AmountParseResult {
  const trimmed = raw.trim();
  if (trimmed.length === 0) return { ok: false, code: 'empty' };

  // Symbol prefix
  const symbolMatch = /^([£$€])\s*(.+)$/.exec(trimmed);
  if (symbolMatch) {
    const currency = SYMBOL_TO_CURRENCY[symbolMatch[1]!];
    return parseNumber(symbolMatch[2]!, currency!);
  }

  // ISO-code prefix (e.g. "GBP 400", "gbp400")
  const codeMatch = /^([a-zA-Z]{3})\s*(.+)$/.exec(trimmed);
  if (codeMatch) {
    const code = codeMatch[1]!.toUpperCase() as Currency;
    if (!SUPPORTED.includes(code)) return { ok: false, code: 'unknown_currency' };
    return parseNumber(codeMatch[2]!, code);
  }

  // Bare number → AED default
  return parseNumber(trimmed, 'AED');
}

function parseNumber(s: string, currency: Currency): AmountParseResult {
  const n = Number(s.trim());
  if (!Number.isFinite(n)) return { ok: false, code: 'no_amount' };
  if (n <= 0) return { ok: false, code: 'non_positive' };
  return { ok: true, amount: n, currency };
}
